import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/Util';
import {MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE} from 'constants/MolbioConstants';
import {Section} from 'constants/NavConstants';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {OligoLatestVersionsQuery} from 'modbio/bulk-edit-mods/__generated/OligoLatestVersionsQuery';
import {reindexStartPos, applyModPattern, previewModPattern} from 'modbio/bulk-edit-mods/ApplyModPatternDAO';
import ApplyToTargetsPane, {ApplyToTargetsPaneProps} from 'modbio/bulk-edit-mods/ApplyToTargetsPane';
import ChooseModPatternPane from 'modbio/bulk-edit-mods/ChooseModPatternPane';
import {ModPatternParams} from 'modbio/bulk-edit-mods/ModPatternConstants';
import {clearPolymerSearchViewSearchParams} from 'molbio/BioentitySearchView';
import notifications from 'notifications';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';

import {SequenceMapPreviewJustify, BulkEditModsResult} from './BulkEditModsConstants';

type OligoLatestVersionsData = OligoLatestVersionsQuery['sequences'][number];

export const DEBOUNCE_MS = 250;

export interface ApplyModPatternModalProps {
  onRequestClose: () => void;
  targetOligoIds: Array<string>;
  nucleotideType: NucleotideType;
  shouldSkipResultsScreenAfterSuccess: boolean;
  isBulk: boolean;
}

export enum ApplyModPatternStepKey {
  CHOOSE_PATTERN = 'CHOOSE_PATTERN',
  APPLY_PATTERN = 'APPLY_PATTERN',
}

export const OLIGO_LATEST_VERSIONS_QUERY = gql`
  query OligoLatestVersionsQuery($ids: [StrictString]!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    sequences(ids: $ids) {
      ... on SequenceNode {
        id
        latestVersionId
        name
        length
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface NonNullOligoData {
  latestVersionId: number;
  name: string;
  length: number;
  id: string;
}

function getOligoData(seqData: OligoLatestVersionsData): NonNullOligoData {
  assert(!isInaccessible(seqData), 'Expected used mod pattern oligo to be accessible.');
  assert(seqData.latestVersionId != null, 'Expected used mod pattern oligo to have non-null version id.');
  return {...seqData, latestVersionId: seqData.latestVersionId};
}

interface ApplyToTargetsPaneDataWrapperProps extends Omit<
  ApplyToTargetsPaneProps,
  'targetOligoNames' | 'templateBasesLength'
> {
  templateOligoData: OligoLatestVersionsData;
  targetOligosData: Array<OligoLatestVersionsData>;
  onSetTemplateOligoVersionId: (id: number | null) => void;
  onSetTargetOligoVersionIds: (ids: Array<number> | null) => void;
  templateOligoVersionId: number | null;
  targetOligoVersionIds: Array<number> | null;
}

function ApplyToTargetsPaneDataWrapper(props: ApplyToTargetsPaneDataWrapperProps): JSX.Element {
  const {
    templateOligoData,
    targetOligosData,
    onSetTemplateOligoVersionId,
    onSetTargetOligoVersionIds,
    templateOligoVersionId,
    targetOligoVersionIds,
    ...paneProps
  } = props;
  const {latestVersionId: templateOligoVersionIdData, length: templateBasesLength} =
    getOligoData(templateOligoData);
  const targetOligosDataNonNull = targetOligosData.map((seqData) => getOligoData(seqData));
  const targetOligoVersionsData = targetOligosDataNonNull.map((data) => data.latestVersionId);
  const targetOligoNames = targetOligosDataNonNull.map((data) => data.name);

  useEffect(() => {
    // Even though these aren't used by the pane, we need to store them as state at the parent Modal layer for
    // the modal submit function, and we'll only have the data at this point of the component tree.
    if (templateOligoVersionIdData !== templateOligoVersionId) {
      onSetTemplateOligoVersionId(templateOligoVersionIdData);
    }
    if (!isEqual(targetOligoVersionsData, targetOligoVersionIds)) {
      onSetTargetOligoVersionIds(targetOligoVersionsData);
    }
  }, [
    templateOligoVersionIdData,
    templateOligoVersionId,
    onSetTemplateOligoVersionId,
    targetOligoVersionsData,
    targetOligoVersionIds,
    onSetTargetOligoVersionIds,
  ]);

  return (
    <ApplyToTargetsPane
      {...paneProps}
      templateBasesLength={templateBasesLength}
      targetOligoNames={targetOligoNames}
    />
  );
}

export default function ApplyModPatternModal({
  targetOligoIds,
  onRequestClose,
  nucleotideType,
  shouldSkipResultsScreenAfterSuccess,
  isBulk,
}: ApplyModPatternModalProps): JSX.Element {
  useEffect(() => {
    clearPolymerSearchViewSearchParams(
      [OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[nucleotideType]],
      Section.MOD_PATTERNS
    );
  }, [nucleotideType]);

  const [templateOligoId, setTemplateOligoId] = useState<string | null>(null);
  const [justify, setJustify] = useState<SequenceMapPreviewJustify>(SequenceMapPreviewJustify.LEFT);
  const [customStartPosition, setCustomStartPosition] = useState<number | null>(null);
  const debouncedCustomStartPosition = useDebounce(customStartPosition, DEBOUNCE_MS);
  const [applyBaseModifications, setApplyBaseModifications] = useState<boolean>(false);
  const [overwriteExistingModifications, setOverwriteExistingModifications] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<Array<BulkEditModsResult> | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [templateOligoVersionId, setTemplateOligoVersionId] = useState<number | null>(null);
  const [targetOligoVersionIds, setTargetOligoVersionIds] = useState<Array<number> | null>(null);
  const [submissionMessage, setSubmissionMessage] = useState<InlineBannerProps | null>(null);
  const statusByStep = {
    [ApplyModPatternStepKey.CHOOSE_PATTERN]:
      templateOligoId != null ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    [ApplyModPatternStepKey.APPLY_PATTERN]: submitted ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
  };
  const [currentStepKey, setCurrentStepKey] = useState(ApplyModPatternStepKey.CHOOSE_PATTERN);
  const [shouldClone, setShouldClone] = useState(false);
  const [folderId, setFolderId] = useState(FolderDefaults.get(FolderDefaultsKey.OLIGO));

  const callPreviewOperation = async (
    params: Omit<ModPatternParams, 'shouldClone' | 'folderId'>
  ): Promise<void> => {
    setLoadingPreview(true);
    const resultsData = await previewModPattern(params);
    setResults(resultsData);
    setLoadingPreview(false);
  };

  useEffect(() => {
    if (
      currentStepKey === ApplyModPatternStepKey.APPLY_PATTERN &&
      templateOligoVersionId != null &&
      targetOligoVersionIds != null
    ) {
      callPreviewOperation({
        templateVersionId: templateOligoVersionId,
        targetVersionIds: targetOligoVersionIds,
        overwriteExistingMods: overwriteExistingModifications || applyBaseModifications,
        applyBaseMods: applyBaseModifications,
        justify,
        startPos: reindexStartPos(debouncedCustomStartPosition),
      });
    }
  }, [
    targetOligoVersionIds,
    templateOligoVersionId,
    justify,
    applyBaseModifications,
    overwriteExistingModifications,
    debouncedCustomStartPosition,
    currentStepKey,
  ]);

  const callApplyOperation = async (params: ModPatternParams): Promise<void> => {
    setLoadingPreview(true);
    LoadingScreen.show('Applying modification pattern...');
    setSubmitted(true);
    try {
      const resultsData = await applyModPattern(params, isBulk);
      setResults(resultsData);
      await FileStates.get().updateSequenceVersions(targetOligoIds);

      const numSuccess = resultsData.filter((result) => result.error == null).length;
      const numFail = resultsData.length - numSuccess;

      if (numFail === 0) {
        const entityDescriptor = pluralize(targetOligoIds.length, 'target');
        const successMessage = shouldClone
          ? `Created and successfully applied modification pattern to ${targetOligoIds.length.toString()} ${entityDescriptor}`
          : `Modification pattern applied successfully to ${entityDescriptor}`;
        if (shouldSkipResultsScreenAfterSuccess) {
          notifications.success(successMessage);
          onRequestClose();
        } else {
          setSubmissionMessage({
            bannerType: InlineBannerType.SUCCESS,
            text: `${successMessage}. Please review the results below.`,
          });
        }
      } else {
        setSubmissionMessage({
          bannerType: InlineBannerType.WARNING,
          text: `Modification pattern was successfully applied to ${numSuccess} ${pluralize(
            numSuccess,
            'target'
          )}, and could not be applied to ${numFail} ${pluralize(
            numFail,
            'target'
          )}. Please review the results below.`,
        });
      }
    } catch (e) {
      setResults([]);
      setSubmissionMessage({
        bannerType: InlineBannerType.ERROR,
        text: 'Something went wrong while applying the modification pattern. Please retry the operation with the same settings.',
      });
      throw e;
    } finally {
      setLoadingPreview(false);
      LoadingScreen.hide();
    }
  };

  const submitModal = (): void => {
    assert(
      templateOligoVersionId != null && targetOligoVersionIds != null,
      'Template should only be applied when oligo data is set.'
    );
    let modPatternParams: ModPatternParams;
    const sharedParams = {
      templateVersionId: templateOligoVersionId,
      targetVersionIds: targetOligoVersionIds,
      overwriteExistingMods: overwriteExistingModifications || applyBaseModifications,
      applyBaseMods: applyBaseModifications,
      justify,
      startPos: reindexStartPos(debouncedCustomStartPosition),
    };
    if (shouldClone) {
      assert(folderId != null, 'Folder must be selected when cloning');
      modPatternParams = {
        ...sharedParams,
        shouldClone: true,
        folderId,
      };
    } else {
      modPatternParams = {
        ...sharedParams,
        shouldClone: false,
        folderId: null,
      };
    }
    callApplyOperation(modPatternParams);
  };

  const buttons =
    currentStepKey === ApplyModPatternStepKey.CHOOSE_PATTERN
      ? [
          {text: 'Cancel', cancel: true},
          {
            text: 'Next',
            disabled: templateOligoId == null,
            onClick: () => {
              trackEvent(MODBIO_ANALYTICS_ACTIONS.REACHED_MOD_PATTERN_APPLY_PANE);
              setCurrentStepKey(ApplyModPatternStepKey.APPLY_PATTERN);
            },
            submit: true,
          },
        ]
      : submitted
        ? [
            {
              text: 'Close',
              submit: true,
              onClick: () => {
                onRequestClose();
              },
              disabled: loadingPreview,
            },
          ]
        : [
            {
              text: 'Back',
              onClick: () => {
                setCurrentStepKey(ApplyModPatternStepKey.CHOOSE_PATTERN);
              },
              disabled: loadingPreview,
            },
            {
              text: shouldClone ? 'Create' : 'Apply',
              submit: true,
              onClick: submitModal,
              disabled:
                loadingPreview ||
                !results?.some((result) => result.error == null) ||
                (shouldClone && folderId == null),
            },
          ];

  const navSteps = [
    {
      key: ApplyModPatternStepKey.CHOOSE_PATTERN,
      title: 'Choose pattern',
    },
    {
      key: ApplyModPatternStepKey.APPLY_PATTERN,
      title: 'Apply pattern',
    },
  ];

  return (
    <Modal
      title="Apply modification pattern"
      large={true}
      buttons={buttons}
      onRequestClose={onRequestClose}
      fullWidth={true}
      fullHeight={true}
      scrollable={false}
      dataTestAttrs={{component: 'ApplyModPatternModal', element: 'root'}}
      bodyPadded={false}
    >
      <div className={css(styles.bodyContainer)}>
        <div className={css(styles.stepNav)}>
          <StepNav<ApplyModPatternStepKey>
            steps={navSteps}
            statusByStep={statusByStep}
            currentStepKey={currentStepKey}
            onUpdateCurrentStep={() => {}}
          />
        </div>
        {currentStepKey === ApplyModPatternStepKey.CHOOSE_PATTERN && (
          <>
            <ChooseModPatternPane
              targetOligoIds={targetOligoIds}
              templateOligoId={templateOligoId}
              onSetTemplateOligoId={setTemplateOligoId}
              nucleotideType={nucleotideType}
            />
          </>
        )}
        {currentStepKey === ApplyModPatternStepKey.APPLY_PATTERN && (
          <BenchlingQuery<OligoLatestVersionsQuery>
            query={OLIGO_LATEST_VERSIONS_QUERY}
            variables={{ids: [templateOligoId!, ...targetOligoIds]}}
            renderLoading={() => <CenteredHorizontalThrobber />}
            fetchPolicy="network-only"
          >
            {({sequences}) => (
              <ApplyToTargetsPaneDataWrapper
                targetOligoIds={targetOligoIds}
                templateOligoId={templateOligoId!}
                justify={justify}
                onSetJustify={setJustify}
                customStartPosition={customStartPosition}
                onSetCustomStartPosition={setCustomStartPosition}
                debouncedCustomStartPosition={debouncedCustomStartPosition}
                applyBaseModifications={applyBaseModifications}
                onSetApplyBaseModifications={setApplyBaseModifications}
                overwriteExistingModifications={overwriteExistingModifications}
                onSetOverwriteExistingModifications={setOverwriteExistingModifications}
                results={results}
                loadingPreview={loadingPreview}
                submitted={submitted}
                nucleotideType={nucleotideType}
                submissionMessage={submissionMessage}
                templateOligoData={sequences[0]}
                targetOligosData={sequences.slice(1)}
                onSetTemplateOligoVersionId={setTemplateOligoVersionId}
                onSetTargetOligoVersionIds={setTargetOligoVersionIds}
                templateOligoVersionId={templateOligoVersionId}
                targetOligoVersionIds={targetOligoVersionIds}
                shouldClone={shouldClone}
                setShouldClone={setShouldClone}
                numSuccessfulResults={results?.filter((result) => result.error == null).length ?? 0}
                isBulk={isBulk}
                folderId={folderId}
                setFolderId={setFolderId}
              />
            )}
          </BenchlingQuery>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  stepNav: {
    padding: 16,
    flex: '0 0 auto',
  },
});
